import type { ReactNode } from 'react';

interface TableFrameProps {
  /** The header cells (`<th>`), placed in a single row. */
  head: ReactNode;
  children: ReactNode;
  /**
   * Column widths (any CSS width; `undefined` takes the remaining space). When
   * given, the table is laid out to the card's width and wraps its cells
   * instead of scrolling sideways.
   */
  columnWidths?: Array<string | undefined>;
}

/** Bordered compact table, styled like ReportTable: scrolls sideways unless it has column widths. */
export function TableFrame({ head, children, columnWidths }: TableFrameProps) {
  const fit = columnWidths !== undefined;
  return (
    <div className={`${fit ? '' : 'overflow-x-auto '}border border-adv-border rounded-adv-lg bg-adv-card`}>
      <table className={`adv-table adv-table--compact ${fit ? 'w-full table-fixed' : 'min-w-full'}`}>
        {fit && (
          <colgroup>
            {columnWidths.map((width, index) => (
              <col key={index} style={width ? { width } : undefined} />
            ))}
          </colgroup>
        )}
        <thead className="bg-adv-sunken">
          <tr>{head}</tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

interface CellProps {
  children?: ReactNode;
  className?: string;
  colSpan?: number;
  title?: string;
}

export function Th({ children, numeric = false }: { children: ReactNode; numeric?: boolean }) {
  return <th className={`px-3 py-2 ${numeric ? 'text-right' : 'text-left'}`}>{children}</th>;
}

/** Text cell. */
export function Td({ children, className = '', colSpan, title }: CellProps) {
  return (
    <td className={`px-3 py-2 align-top ${className}`} colSpan={colSpan} title={title}>
      {children}
    </td>
  );
}

/** Right-aligned figure cell. */
export function NumTd({ children, className = '', colSpan, title }: CellProps) {
  return (
    <td
      className={`px-3 py-2 align-top text-right adv-num whitespace-nowrap ${className}`}
      colSpan={colSpan}
      title={title}
    >
      {children}
    </td>
  );
}
